import React, { useState } from 'react';
import { StanButton, StanModal, StanMultiSelect } from '@/components';
import type { ImportErrorState } from './AddContactFlow';
import { sanitizeHtml } from '@/lib';
import { format } from 'date-fns';

interface ImportContactsConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (tag: string) => void;
    onFileSelected: (file: File, count: number) => void;
    contactCount: number;
    isImporting: boolean;
    importError?: ImportErrorState;
}

export function ImportContactsConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    onFileSelected,
    contactCount,
    isImporting,
    importError = { code: null },
}: ImportContactsConfirmationModalProps) {
    const [tags, setTags] = useState<string[]>([]);
    const [acknowledged, setAcknowledged] = useState(false);

    const getDefaultTag = () => format(new Date(), "'Imported on' MMM d, yyyy - h:mm a");
    const defaultTag = getDefaultTag();

    const handleConfirm = () => {
        if (!acknowledged) return;
        // Use the first tag if provided, otherwise use the default tag
        const tag = tags.length > 0 && tags[0] ? tags[0] : defaultTag;
        onConfirm(tag || '');
    };

    const handleClose = () => {
        setTags([]);
        setAcknowledged(false);
        onClose();
    };

    // Set default tag when modal opens (regenerate timestamp each time)
    React.useEffect(() => {
        if (isOpen) {
            setTags([getDefaultTag()]);
        }
    }, [isOpen]);

    // Determine modal title based on error state
    const getModalTitle = (): string => {
        if (importError?.code === 'missing-fields') return 'Could not find contacts.';
        if (importError?.code === 'row-count-exceeded') return 'Too many contacts';
        if (importError?.code === 'partially-imported') return 'Successfully imported some contacts.';
        if (importError?.code) return 'Oops';
        if (contactCount === 0) return 'Could not find contacts.';
        return `Import ${contactCount} contact${contactCount !== 1 ? 's' : ''}?`;
    };

    // Determine modal subheading based on error state
    const getModalSubheading = (): string => {
        if (importError?.code === 'row-count-exceeded') {
            return 'The file exceeds the maximum row limit of 5000.';
        }
        if (importError?.message) {
            return importError.message;
        }
        if (contactCount === 0) {
            return 'Double check that your file has at least one contact record.';
        }
        if (importError?.code === null && contactCount > 0) {
            return 'Tag your contacts to find them later!';
        }
        return '';
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Read CSV file to count contacts
            try {
                const text = await file.text();
                const lines = text.trim().split('\n');
                // Subtract 1 for header row
                const count = Math.max(0, lines.length - 1);

                // Notify parent to update file and contact count
                onFileSelected(file, count);
            } catch (error) {
                console.error('Error reading CSV file:', error);
            }
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    };

    const hasError = importError?.code !== null;
    const showContent = !hasError && contactCount > 0;

    return (
        <StanModal
            id='import-contacts-confirmation-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title={getModalTitle()}
            size='sm'
        >
            <div className='flex flex-col gap-6'>
                {/* Subtitle */}
                {getModalSubheading() && (
                    <p className='text-gray text-label-sm-regular text-center'>{getModalSubheading()}</p>
                )}

                {/* Error Hint */}
                {importError?.hint && (
                    <div
                        className='text-gray text-label-sm-regular text-center'
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(importError.hint) }}
                    />
                )}

                {/* Content only shown when no error and contacts found */}
                {showContent && (
                    <>
                        {/* Tag Input */}
                        <StanMultiSelect
                            value={tags}
                            options={[]}
                            onChange={setTags}
                            placeholder='Select a tag'
                            allowCreate
                            disabled={isImporting}
                            maxTags={1}
                            showCounter={false}
                            name='import-tag'
                            id='import-tag'
                        />

                        {/* Acknowledgment Checkbox */}
                        <label className='flex cursor-pointer items-start gap-3'>
                            <input
                                type='checkbox'
                                checked={acknowledged}
                                onChange={e => setAcknowledged(e.target.checked)}
                                disabled={isImporting}
                                className='accent-brand-main mt-1 size-4 cursor-pointer'
                            />
                            <span className='text-dark text-label-sm-regular flex-1'>
                                I acknowledge that I have read and understand the{' '}
                                <a
                                    href='https://assets.stanwith.me/legal/terms-of-service.pdf'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-brand-main hover:underline'
                                >
                                    Terms of Service
                                </a>{' '}
                                and the{' '}
                                <a
                                    href='https://assets.stanwith.me/legal/CASL.pdf'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-brand-main hover:underline'
                                >
                                    Anti-Spam Rules
                                </a>
                                .
                            </span>
                        </label>

                        {/* Begin Import Button */}
                        <StanButton
                            name='begin-import-button'
                            disabled={!acknowledged || isImporting}
                            loading={isImporting}
                            onClick={handleConfirm}
                            className='w-full'
                        >
                            Begin Import
                        </StanButton>
                    </>
                )}

                {/* Show Upload button when there's an error (allows retry) */}
                {hasError && (
                    <div className='flex flex-col gap-4'>
                        <input
                            type='file'
                            accept='.csv'
                            onChange={handleFileChange}
                            className='hidden'
                            id='csv-upload-retry'
                            disabled={isImporting}
                        />

                        <StanButton
                            name='upload-csv-retry-button'
                            color='secondary'
                            disabled={isImporting}
                            onClick={() => document.getElementById('csv-upload-retry')?.click()}
                            className='w-full'
                        >
                            Upload
                        </StanButton>
                    </div>
                )}
            </div>
        </StanModal>
    );
}
